// Modules:
import React, { useRef, useState, useEffect, useContext } from 'react';

// Utilities
import { formatNumber, lightenColor } from 'utilities/utils';
import TopicConfigContext from 'utilities/topicConfigContext';

// Material UI:
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Chart.js:
import { Bar } from 'react-chartjs-2';

// Components:
import Selector from './Selector';

const VerticalBar = ({
    json,
    selection,
    dataUnavailable,
    setDataUnavailable,
    handleDropdownSelect,
    checkIfNull,
    styles,
    setMissingSuppressedModalOpen,
    setChartInfoModalOpen,
}) => {
    // // State // //
    const [chartData, setChartData] = useState({});

    // // Context // //
    const topicConfigContext = useContext(TopicConfigContext);

    // // Refs // //
    const chartRef = useRef();

    // // Effects // //
    useEffect(() => {
        const loadChart = () => {
            setChartData({});
            setDataUnavailable(false);

            if (!json) {
                return;
            }

            const {
                chartConfigs,
                chartDataSets,
                chartYAxisField,
            } = topicConfigContext.tab.chartSettings;

            const getChartData = () => {
                const data = {};

                data.labels = json.map((row) => row[chartYAxisField]);

                const datasetArray = chartDataSets.reduce((acc, setName) => {
                    const configEntry = chartConfigs.find(
                        (c) => c.setName === setName
                    );

                    if (!configEntry) {
                        return acc;
                    }

                    // Shallow clone so we never mutate the shared context object
                    const dataset = { ...configEntry };

                    dataset.data = json.map((row) => row[setName] ?? null);

                    dataset.backgroundColor = dataset.color;
                    dataset.hoverBackgroundColor = lightenColor(dataset.color);

                    acc.push(dataset);
                    return acc;
                }, []);

                const allNull = checkIfNull(datasetArray);
                if (allNull) {
                    setDataUnavailable(true);
                }

                data.datasets = datasetArray;
                setChartData(data);
            };

            getChartData();
        };

        loadChart();
    }, [
        json,
        selection,
        checkIfNull,
        topicConfigContext.tab,
        setDataUnavailable,
    ]);

    const {
        chartTitle,
        displayChartTitle,
        displayChartSubtitle,
        chartSubtitle,
        displayXAxisLabel,
        xAxisLabel,
        displayYAxisLabel,
        yAxisLabel,
        chartDataYears: dataYears,
    } = topicConfigContext.tab.chartSettings;

    return (
        <>
            <Grid container justify="center">
                {dataYears && selection && (
                    <Selector
                        currentTab={topicConfigContext.tab}
                        chartDataYears={dataYears}
                        handleDropdownSelect={handleDropdownSelect}
                        selection={selection}
                        styles={styles}
                    ></Selector>
                )}
            </Grid>

            {dataUnavailable ? (
                <Grid item xs={12}>
                    <div className="data-unavailable">
                        <Alert
                            sx={styles}
                            severity="info"
                            onClick={() => setMissingSuppressedModalOpen(true)}
                        >
                            Data are not available to display for {selection}.
                        </Alert>
                    </div>
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <div className="chart-container vertical-season-bars">
                        {Object.keys(chartData).length ? (
                            <Bar
                                ref={chartRef}
                                data={chartData}
                                width={100}
                                height={350}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        title: {
                                            display: displayChartTitle,
                                            text: `${chartTitle} ${selection}`,
                                        },
                                        subtitle: {
                                            display:
                                                displayChartSubtitle || false,
                                            text: chartSubtitle || '',
                                        },
                                        tooltip: {
                                            mode: 'index',
                                            intersect: false,
                                            filter: (tooltipItem) =>
                                                tooltipItem.raw !== null,
                                            callbacks: {
                                                label: (tooltipItem) =>
                                                    `${
                                                        tooltipItem.dataset
                                                            .label
                                                    }: ${formatNumber(
                                                        tooltipItem.raw
                                                    )}%`,
                                            },
                                        },
                                        legend: {
                                            position: 'bottom',
                                        },
                                    },
                                    scales: {
                                        x: {
                                            title: {
                                                display: displayXAxisLabel,
                                                text: xAxisLabel,
                                            },
                                        },
                                        y: {
                                            beginAtZero: true,
                                            title: {
                                                display: displayYAxisLabel,
                                                text: yAxisLabel || '',
                                            },
                                            ticks: {
                                                callback: (value) =>
                                                    `${value.toFixed(1)}%`,
                                            },
                                        },
                                    },
                                }}
                            />
                        ) : null}
                    </div>
                    {Object.keys(chartData).length ? (
                        <div className="chart-info-modal-trigger-container">
                            <Button
                                size="small"
                                onClick={() => setChartInfoModalOpen(true)}
                            >
                                <InfoOutlinedIcon />
                            </Button>
                        </div>
                    ) : null}
                </Grid>
            )}
        </>
    );
};

export default VerticalBar;
